import React, { useState, useMemo } from "react";
import { usePatientsTasks } from "../hooks/useClinic";
import { useTablePreferences } from "../hooks/useTablePreferences";
import { useListViews } from "../hooks/useListViews";
import { CustomButton } from "../components/ui/CustomButton";
import { User, GetPatientsTasksPayload, ListView, ListViewData, MeetingType, MedicationType, ClinicUserApiResponse, MedicalOpinion } from "../types/api";
import { Layout } from "../components/layout/Layout";
import { ManageColumnsModal, ColumnConfig } from "../components/modals/ManageColumnsModal";
import { SaveViewModal } from "../components/modals/SaveViewModal";
import { MeetingCountModal } from "../components/modals/MeetingCountModal";
import { EditPatientModal } from "../components/modals/EditPatientModal";
import { NewMeetingModal } from "../components/modals/NewMeetingModal";
import { EditCommentsModal } from "../components/modals/EditCommentsModal";
import { ViewMeetingsModal } from "../components/modals/ViewMeetingsModal";
import { Parent2SignModal } from "../components/modals/Parent2SignModal";
import { EditEmailModal } from "../components/modals/EditEmailModal";
import { PSYSummaryModal } from "../components/modals/PSYSummaryModal";
import { CMSummaryModal } from "../components/modals/CMSummaryModal";
import { ApproveMaccabiModal } from "../components/modals/ApproveMaccabiModal";
import { ApproveClalitModal } from "../components/modals/ApproveClalitModal";
import { MaccabiTransferModal } from "../components/modals/MaccabiTransferModal";
import { TaliazTransferModal } from "../components/modals/TaliazTransferModal";
import { SingleGuardianVerificationModal } from "../components/modals/SingleGuardianVerificationModal";
import { MissingDocumentsModal } from "../components/modals/MissingDocumentsModal";
import { CaseClosureApprovalModal } from "../components/modals/CaseClosureApprovalModal";
import { MedicalOpinionObligationApprovalModal } from "../components/modals/MedicalOpinionObligationApprovalModal";
import { MedicalOpinionLetterModal } from "../components/modals/MedicalOpinionLetterModal";
import { ViewSelector } from "../components/ui/ViewSelector";

import { ContextMenu } from "../components/ui/ContextMenu";
import apiService from "../services/api";
import { DiagnosisCode } from "../types/api";
import { saveLastViewId, getLastViewId, clearLastViewId } from "../utils/viewPersistence";
import { canScheduleMeeting } from '../utils/permissions';

interface TasksScreenProps {
    user: User;
    onLogout: () => void;
    onNavigate?: (screen: "calendar" | "patients" | "tasks" | "internal-tasks" | "patient-page" | "patient" | "users" | "connections" | "billing" | "whatsapp" | "adhoc-meetings" | "ai-support", patientId?: string | number, meetingId?: string | number) => void;
    medicationTypes: MedicationType[];
    users: ClinicUserApiResponse[];
    meetingTypes: MeetingType[];
    diagnosisCodes?: DiagnosisCode[];
    clinics?: any;
    taskCount?: number;
    whatsappUnreadCount?: number;
}

export const TasksScreen: React.FC<TasksScreenProps> = ({
    user,
    onLogout,
    onNavigate,
    medicationTypes,
    users,
    meetingTypes: propsMeetingTypes,
    diagnosisCodes: propsDiagnosisCodes,
    clinics: propsClinics,
    taskCount,
    whatsappUnreadCount,
}) => {
    // Check if user is admin first (needed for state initialization)
    const isAdmin = useMemo(() => {
        return user?.role === 'admin' || user?.role === 'Admin';
    }, [user?.role]);

    // Calculate default "only mine" value based on user role and type
    const defaultOnlyMy = useMemo(() => {
        const isAdminRole = user?.role === 'admin' || user?.role === 'Admin';
        const isCaseManagerOrDoctor = user.type === 'doctor' || user.type === 'case_manager';
        return isAdminRole && isCaseManagerOrDoctor;
    }, [user?.role, user.type]);

    const [selectedClinicId, setSelectedClinicId] = useState<number>(0);
    const [search, setSearch] = useState("");

    // Filter states
    const [selectedUser, setSelectedUser] = useState<number | "">(""); // issue_owner
    const [selectedIssue, setSelectedIssue] = useState<string>("");
    const [selectedIssueType, setSelectedIssueType] = useState<string>("");
    const [selectedSeverity, setSelectedSeverity] = useState<string>("");
    const [onlyMy, setOnlyMy] = useState<boolean>(defaultOnlyMy);

    // Define all available columns
    const ALL_COLUMNS: ColumnConfig[] = [
        { id: "issue", label: "Issue", visible: true },
        { id: "issue_type", label: "Issue Type", visible: true },
        { id: "issue_severity", label: "Severity", visible: true },
        { id: "issue_date", label: "Issue date", visible: true },
        { id: "HM_ID", label: "HM ID", visible: true },
        { id: "first_name", label: "First name", visible: true },
        { id: "last_name", label: "Last name", visible: true },
        { id: "case_manager_id", label: "Case Manager", visible: true },
        { id: "doctor_id", label: "Psychiatrist", visible: true },
        { id: "meeting_count_total_text", label: "Meeting Number", visible: true },
        { id: "next_meeting_start", label: "Next meeting", visible: true },
        { id: "next_meeting_team_url", label: "Next meeting link", visible: true },
        { id: "task", label: "Notes", visible: true },
        { id: "main_phone", label: "Main phone", visible: true },
        { id: "patient_type", label: "Age group", visible: false },
        { id: "status", label: "Status", visible: false },
        { id: "email", label: "Email", visible: false },
    ];

    // Default preferences
    const defaultPreferences = {
        columnOrder: ALL_COLUMNS.map(col => col.id),
        columnWidths: {},
        columnVisibility: ALL_COLUMNS.reduce((acc, col) => {
            acc[col.id] = col.visible;
            return acc;
        }, {} as Record<string, boolean>),
        sortColumn: "issue_date",
        sortOrder: "desc" as const,
    };

    // Use the preferences hook
    const { preferences, updatePreferences, resetToDefaults } = useTablePreferences({
        storageKey: 'tasks-table-preferences',
        defaultPreferences,
    });

    // Extract values from preferences
    const [sortColumn, setSortColumn] = useState(preferences.sortColumn);
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">(preferences.sortOrder);
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>(preferences.columnWidths);
    const [columnOrder, setColumnOrder] = useState<string[]>(preferences.columnOrder);
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(preferences.columnVisibility);

    const token = localStorage.getItem("token") || "";

    // Modal state
    const [isManageColumnsOpen, setIsManageColumnsOpen] = useState(false);
    const [isSaveViewModalOpen, setIsSaveViewModalOpen] = useState(false);
    const [currentViewId, setCurrentViewId] = useState<number | null>(null);

    // Track the base view that was loaded, even after currentViewId is cleared
    const [baseViewId, setBaseViewId] = useState<number | null>(null);
    const [isMeetingCountModalOpen, setIsMeetingCountModalOpen] = useState(false);
    const [selectedTaskForMeetings, setSelectedTaskForMeetings] = useState<any>(null);
    const [isNewMeetingModalOpen, setIsNewMeetingModalOpen] = useState(false);
    const [selectedTaskForNewMeeting, setSelectedTaskForNewMeeting] = useState<any>(null);
    const [isEditPatientModalOpen, setIsEditPatientModalOpen] = useState(false);
    const [selectedTaskForEditInfo, setSelectedTaskForEditInfo] = useState<any>(null);
    const [isLoadingPatientData, setIsLoadingPatientData] = useState(false);
    const [isSavingPatientInfo, setIsSavingPatientInfo] = useState(false);
    const [isViewMeetingsModalOpen, setIsViewMeetingsModalOpen] = useState(false);
    const [selectedTaskForViewMeetings, setSelectedTaskForViewMeetings] = useState<any>(null);
    const [isParent2SignModalOpen, setIsParent2SignModalOpen] = useState(false);
    const [parent2SignLink, setParent2SignLink] = useState<string>("");
    const [isEditEmailModalOpen, setIsEditEmailModalOpen] = useState(false);
    const [selectedTaskForEditEmail, setSelectedTaskForEditEmail] = useState<any>(null);
    const [isSavingEmail, setIsSavingEmail] = useState(false);
    const [isPSYSummaryModalOpen, setIsPSYSummaryModalOpen] = useState(false);
    const [selectedTaskForPSYSummary, setSelectedTaskForPSYSummary] = useState<any>(null);
    const [isCMSummaryModalOpen, setIsCMSummaryModalOpen] = useState(false);
    const [selectedTaskForCMSummary, setSelectedTaskForCMSummary] = useState<any>(null);
    const [isApproveMaccabiModalOpen, setIsApproveMaccabiModalOpen] = useState(false);
    const [selectedTaskForApproveMaccabi, setSelectedTaskForApproveMaccabi] = useState<any>(null);
    const [isApproveClalitModalOpen, setIsApproveClalitModalOpen] = useState(false);
    const [selectedTaskForApproveClalit, setSelectedTaskForApproveClalit] = useState<any>(null);
    const [isMaccabiTransferModalOpen, setIsMaccabiTransferModalOpen] = useState(false);
    const [selectedTaskForMaccabiTransfer, setSelectedTaskForMaccabiTransfer] = useState<any>(null);
    const [isTaliazTransferModalOpen, setIsTaliazTransferModalOpen] = useState(false);
    const [selectedTaskForTaliazTransfer, setSelectedTaskForTaliazTransfer] = useState<any>(null);
    const [isEditCommentsModalOpen, setIsEditCommentsModalOpen] = useState(false);
    const [selectedTaskForEdit, setSelectedTaskForEdit] = useState<any>(null);
    const [isSavingComments, setIsSavingComments] = useState(false);
    const [isSingleGuardianModalOpen, setIsSingleGuardianModalOpen] = useState(false);
    const [selectedTaskForSingleGuardian, setSelectedTaskForSingleGuardian] = useState<any>(null);
    const [isSavingSingleGuardian, setIsSavingSingleGuardian] = useState(false);
    const [isMissingDocumentsModalOpen, setIsMissingDocumentsModalOpen] = useState(false);
    const [selectedTaskForMissingDocuments, setSelectedTaskForMissingDocuments] = useState<any>(null);
    const [isCaseClosureModalOpen, setIsCaseClosureModalOpen] = useState(false);
    const [selectedTaskForCaseClosure, setSelectedTaskForCaseClosure] = useState<any>(null);
    const [isMedicalOpinionApprovalModalOpen, setIsMedicalOpinionApprovalModalOpen] = useState(false);
    const [isMedicalOpinionLetterModalOpen, setIsMedicalOpinionLetterModalOpen] = useState(false);
    const [selectedTaskForMedicalOpinion, setSelectedTaskForMedicalOpinion] = useState<any>(null);
    const [selectedMedicalOpinion, setSelectedMedicalOpinion] = useState<MedicalOpinion | null>(null);
    const [isLoadingMedicalOpinion, setIsLoadingMedicalOpinion] = useState(false);


    // Context menu state
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; task: any } | null>(null);

    // Status message state
    const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error' | 'loading'; text: string } | null>(null);

    // Both medical opinion tasks carry medical_opinion_id, so the request is fetched by id
    // rather than by pulling the patient's whole list and filtering. They differ only in
    // which modal the record lands in: the secretary's obligation screen, or the assigned
    // doctor's letter screen.
    const openMedicalOpinionTask = React.useCallback(async (task: any, kind: 'obligation' | 'letter') => {
        setSelectedTaskForMedicalOpinion(task);
        setSelectedMedicalOpinion(null);
        setIsLoadingMedicalOpinion(true);
        if (kind === 'obligation') {
            setIsMedicalOpinionApprovalModalOpen(true);
        } else {
            setIsMedicalOpinionLetterModalOpen(true);
        }
        try {
            const opinion = await apiService.getMedicalOpinion({
                token,
                medical_opinion_id: task.medical_opinion_id,
            });
            setSelectedMedicalOpinion(opinion);
        } catch (err: any) {
            // A missing record also comes back as 403 - the backend does not distinguish
            // "gone" from "not yours", so the message has to cover both.
            setStatusMessage({
                type: 'error',
                text: err?.response?.status === 403
                    ? 'This medical opinion request is no longer available, or you are not authorized to open it'
                    : 'Failed to load the medical opinion request',
            });
            setIsMedicalOpinionApprovalModalOpen(false);
            setIsMedicalOpinionLetterModalOpen(false);
            setSelectedTaskForMedicalOpinion(null);
        } finally {
            setIsLoadingMedicalOpinion(false);
        }
    }, [token]);

    // List Views integration
    const {
        privateViews,
        publicViews,
        defaultView,
        createListView,
        updateListView,
        deleteListView,
        isLoading: isLoadingViews,
        isCreating,
    } = useListViews({
        userId: user?.id as number,
        listName: 'tasks',
        token,
        enabled: !!user?.id && !!token,
    });

    // Track if we've already loaded the initial view to prevent re-loading when view is cleared
    const hasLoadedInitialView = React.useRef(false);

    // Load saved view on mount
    React.useEffect(() => {
        if (isLoadingViews) return;
        if (hasLoadedInitialView.current) return;

        const savedViewId = getLastViewId('tasks');
        if (savedViewId !== null) {
            const allViews = [...privateViews, ...publicViews];
            const savedView = allViews.find(v => v.id === savedViewId);
            if (savedView) {
                handleLoadView(savedView);
                hasLoadedInitialView.current = true;
            } else {
                // View was deleted, clear the saved ID
                clearLastViewId('tasks');
            }
        } else if (defaultView) {
            handleLoadView(defaultView);
            hasLoadedInitialView.current = true;
        } else {
            hasLoadedInitialView.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingViews, privateViews, publicViews, defaultView]);

    // Column resizing state
    const [resizingColumn, setResizingColumn] = useState<string | null>(null);
    const [startX, setStartX] = useState<number>(0);
    const [startWidth, setStartWidth] = useState<number>(0);


    // Sync preferences when state changes
    React.useEffect(() => {
        updatePreferences({
            columnOrder,
            columnWidths,
            columnVisibility,
            sortColumn,
            sortOrder,
        });
    }, [columnOrder, columnWidths, columnVisibility, sortColumn, sortOrder, updatePreferences]);

    // Use clinics from props
    const clinics = useMemo(
        () => propsClinics?.items || [],
        [propsClinics?.items]
    );

    // Use passed-in users instead of fetching separately
    const userNameMap = useMemo(() => {
        const map: Record<number, string> = {};
        users.forEach((u: any) => {
            map[u.id] = u.name || u.email;
        });
        return map;
    }, [users]);

    const getUserName = React.useCallback((userId: number): string => {
        return userNameMap[userId] || "";
    }, [userNameMap]);

    const tasksPayload: GetPatientsTasksPayload = useMemo(
        () => {
            const payload: GetPatientsTasksPayload = {
                token,
                clinic_id: selectedClinicId,
            };

            // If not admin, add current user_id
            if (!isAdmin) {
                payload.user_id = typeof user.id === 'number' ? user.id : parseInt(user.id as string, 10);
            }

            return payload;
        },
        [token, selectedClinicId, isAdmin, user.id]
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
    };

    const [removedTaskIds, setRemovedTaskIds] = useState<Set<string>>(new Set());

    const tasksQuery = usePatientsTasks(tasksPayload);

    // Identity of a single task row. `task.id` is the PATIENT id, not a task id, so it only
    // identifies a row together with what the task is about: the meeting for meeting tasks,
    // the medical opinion for the opinion tasks. A patient can hold several open opinion
    // requests at once (one per opinion type), and without medical_opinion_id they would all
    // share one key -- handling one would hide the rest until the refetch lands.
    const getTaskKey = (task: any) =>
        `${task.id}-${task.issue}-${task.issue_meeting_id || ''}-${task.medical_opinion_id || ''}`;

    // Function to remove a task from the list without refetching
    const removeTaskFromList = React.useCallback((task: any) => {
        console.log('[TasksScreen] Removing task from list:', {
            taskId: task.id,
            taskIssue: task.issue,
            issueMeetingId: task.issue_meeting_id,
            medicalOpinionId: task.medical_opinion_id,
        });

        const taskKey = getTaskKey(task);

        setRemovedTaskIds(prev => {
            const newSet = new Set(prev);
            newSet.add(taskKey);
            console.log('[TasksScreen] Task marked for removal:', taskKey);
            return newSet;
        });
    }, []);

    // Extract unique filter options from tasks based on currently applied filters
    // Each filter shows only options that exist when other filters are applied
    const filterOptions = useMemo(() => {
        let tasks = tasksQuery.data || [];

        // Helper function to get filtered tasks excluding a specific filter
        const getTasksExcludingFilter = (excludeFilter: 'user' | 'issue' | 'issueType' | 'severity') => {
            let filtered = tasks;

            if (excludeFilter !== 'user' && selectedUser !== "") {
                filtered = filtered.filter(task => task.issue_owner === selectedUser);
            }
            if (excludeFilter !== 'issue' && selectedIssue) {
                filtered = filtered.filter(task => task.issue === selectedIssue);
            }
            if (excludeFilter !== 'issueType' && selectedIssueType) {
                filtered = filtered.filter(task => task.issue_type === selectedIssueType);
            }
            if (excludeFilter !== 'severity' && selectedSeverity) {
                filtered = filtered.filter(task => task.issue_severity === selectedSeverity);
            }

            return filtered;
        };

        // Calculate available options for each filter based on other filters
        const usersSet = new Set<number>();
        const issuesSet = new Set<string>();
        const issueTypesSet = new Set<string>();
        const severitiesSet = new Set<string>();

        // Get tasks for each filter (excluding that specific filter)
        const tasksForUsers = getTasksExcludingFilter('user');
        const tasksForIssues = getTasksExcludingFilter('issue');
        const tasksForIssueTypes = getTasksExcludingFilter('issueType');
        const tasksForSeverities = getTasksExcludingFilter('severity');

        tasksForUsers.forEach(task => {
            if (task.issue_owner) usersSet.add(task.issue_owner);
        });
        tasksForIssues.forEach(task => {
            if (task.issue) issuesSet.add(task.issue);
        });
        tasksForIssueTypes.forEach(task => {
            if (task.issue_type) issueTypesSet.add(task.issue_type);
        });
        tasksForSeverities.forEach(task => {
            if (task.issue_severity) severitiesSet.add(task.issue_severity);
        });

        return {
            users: Array.from(usersSet)
                .filter(userId => {
                    const name = getUserName(userId);
                    return name && name.trim() !== '';
                })
                .sort((a, b) => {
                    const nameA = getUserName(a)?.toLowerCase() || '';
                    const nameB = getUserName(b)?.toLowerCase() || '';
                    return nameA.localeCompare(nameB);
                }),
            issues: Array.from(issuesSet).sort(),
            issueTypes: Array.from(issueTypesSet).sort(),
            severities: Array.from(severitiesSet).sort()
        };
    }, [tasksQuery.data, getUserName, selectedUser, selectedIssue, selectedIssueType, selectedSeverity]);

    // Filter and sort tasks
    const filteredAndSortedTasks = useMemo(() => {
        let tasks = tasksQuery.data || [];

        // Filter by user (issue_owner) or only my tasks
        if (onlyMy) {
            const currentUserId = typeof user.id === 'number' ? user.id : parseInt(user.id as string, 10);
            tasks = tasks.filter(task => task.issue_owner === currentUserId);
        } else if (selectedUser !== "") {
            tasks = tasks.filter(task => task.issue_owner === selectedUser);
        }

        // Filter by issue
        if (selectedIssue) {
            tasks = tasks.filter(task => task.issue === selectedIssue);
        }

        // Filter by issue type
        if (selectedIssueType) {
            tasks = tasks.filter(task => task.issue_type === selectedIssueType);
        }

        // Filter by severity
        if (selectedSeverity) {
            tasks = tasks.filter(task => task.issue_severity === selectedSeverity);
        }

        // Filter by search
        if (search) {
            const searchLower = search.toLowerCase();
            tasks = tasks.filter(task => {
                // Get mapped user names
                const caseManagerName = getUserName(task.case_manager_id)?.toLowerCase() || '';
                const doctorName = getUserName(task.doctor_id)?.toLowerCase() || '';

                // Search across all relevant fields
                return (
                    task.first_name?.toLowerCase().includes(searchLower) ||
                    task.last_name?.toLowerCase().includes(searchLower) ||
                    task.HM_ID?.toLowerCase().includes(searchLower) ||
                    task.issue?.toLowerCase().includes(searchLower) ||
                    task.issue_type?.toLowerCase().includes(searchLower) ||
                    task.task?.toLowerCase().includes(searchLower) || // Notes field
                    task.email?.toLowerCase().includes(searchLower) ||
                    task.main_phone?.toLowerCase().includes(searchLower) ||
                    task.status?.toLowerCase().includes(searchLower) ||
                    task.patient_type?.toLowerCase().includes(searchLower) ||
                    task.issue_severity?.toLowerCase().includes(searchLower) ||
                    caseManagerName.includes(searchLower) ||
                    doctorName.includes(searchLower)
                );
            });
        }

        // Filter out removed tasks
        tasks = tasks.filter(task => {
            const taskKey = getTaskKey(task);
            return !removedTaskIds.has(taskKey);
        });

        // Sort tasks. Copy first: `tasks` is only guaranteed to be a fresh array because
        // every filter above returns one, and sorting the query data in place would corrupt
        // the React Query cache the day one of them becomes conditional.
        tasks = [...tasks].sort((a, b) => {
            const aValue = (a as any)[sortColumn];
            const bValue = (b as any)[sortColumn];

            if (aValue === bValue) return 0;
            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            const comparison = aValue < bValue ? -1 : 1;
            return sortOrder === "asc" ? comparison : -comparison;
        });

        return tasks;
    }, [tasksQuery.data, selectedUser, selectedIssue, selectedIssueType, selectedSeverity, search, sortColumn, sortOrder, getUserName, onlyMy, user.id, removedTaskIds]);

    const handleRefresh = () => {
        tasksQuery.refetch();
    };

    const handleClearFilters = () => {
        setSelectedUser("");
        setSelectedIssue("");
        setSelectedIssueType("");
        setSelectedSeverity("");
        setOnlyMy(false);
    };

    // Reset user filter for non-admin users (onlyMy is set correctly on init)
    React.useEffect(() => {
        if (!isAdmin) {
            if (selectedUser !== "") {
                setSelectedUser("");
            }
        }
    }, [isAdmin, selectedUser]);

    // Format date
    const formatDate = (timestamp: number | undefined | null) => {
        if (!timestamp || timestamp === 0) return "";
        const date = new Date(timestamp);
        return date.toLocaleDateString("en-GB");
    };

    // Format next meeting
    const formatNextMeetingDateTime = (timestamp: number): string => {
        if (!timestamp || timestamp === 0) return "";

        const meetingDate = new Date(timestamp);
        const now = new Date();

        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);
        const tomorrowStart = new Date(todayEnd);
        const tomorrowEnd = new Date(tomorrowStart.getTime() + 24 * 60 * 60 * 1000);

        const formattedTime = meetingDate.toLocaleTimeString("en-GB", {
            hour: '2-digit',
            minute: '2-digit'
        });

        if (meetingDate >= todayStart && meetingDate < todayEnd) {
            return `Today at ${formattedTime}`;
        } else if (meetingDate >= tomorrowStart && meetingDate < tomorrowEnd) {
            return `Tomorrow at ${formattedTime}`;
        } else {
            const day = String(meetingDate.getDate()).padStart(2, '0');
            const month = String(meetingDate.getMonth() + 1).padStart(2, '0');
            const year = meetingDate.getFullYear();
            return `${day}/${month}/${year} ${formattedTime}`;
        }
    };

    // Get severity color
    const getSeverityColor = (severity: string): string => {
        switch (severity.toLowerCase()) {
            case 'error': return '#ef4444';
            case 'warning': return '#f59e0b';
            case 'info': return '#3b82f6';
            default: return '#6b7280';
        }
    };

    // Get age group color
    const getAgeGroupColor = (patientType: string): string => {
        switch (patientType) {
            case 'Soldier': return '#442586';
            case 'Adult': return '#664ED1';
            case 'Kids': return '#CFC6F9';
            default: return '#D8A5E5';
        }
    };

    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortOrder("desc");
        }
    };

    // Column resize handlers
    const handleResizeStart = (e: React.MouseEvent, column: string, currentWidth: number) => {
        e.preventDefault();
        e.stopPropagation();
        setResizingColumn(column);
        setStartX(e.clientX);
        setStartWidth(currentWidth);
    };

    React.useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!resizingColumn) return;

            const diff = e.clientX - startX;
            const newWidth = Math.max(50, startWidth + diff);

            setColumnWidths(prev => ({
                ...prev,
                [resizingColumn]: newWidth
            }));
        };

        const handleMouseUp = () => {
            setResizingColumn(null);
        };

        if (resizingColumn) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [resizingColumn, startX, startWidth]);





    const ResizableHeader = ({ column, label, nonSortable = false }: { column: string; label: string; nonSortable?: boolean }) => {
        const isSorted = sortColumn === column;
        const columnRef = React.useRef<HTMLTableCellElement>(null);
        const width = columnWidths[column];

        const handleHeaderClick = (e: React.MouseEvent) => {
            const target = e.target as HTMLElement;
            // Don't sort if clicking on resize handle
            if (target.classList.contains('cursor-col-resize') || target.closest('.cursor-col-resize')) {
                return;
            }
            if (!nonSortable) {
                handleSort(column);
            }
        };

        return (
            <th
                ref={columnRef}
                onClick={handleHeaderClick}
                className={`relative px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap ${!nonSortable ? 'hover:bg-gray-100 cursor-pointer' : ''} select-none`}
                style={width ? { width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` } : {}}
            >
                <div className="flex items-center gap-1">
                    {label}
                    {!nonSortable && isSorted && (
                        <span className="text-blue-600">
                            {sortOrder === "asc" ? "↑" : "↓"}
                        </span>
                    )}
                </div>
                <div
                    className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-400 bg-gray-300"
                    onMouseDown={(e) => handleResizeStart(e, column, columnRef.current?.offsetWidth || 150)}
                    onClick={(e) => e.stopPropagation()}
                />
            </th>
        );
    };

    // Modal handlers
    const handleManageColumns = () => {
        setIsManageColumnsOpen(true);
    };

    const handleSaveColumns = (columns: ColumnConfig[]) => {
        const newOrder = columns.map(col => col.id);
        const newVisibility = columns.reduce((acc, col) => {
            acc[col.id] = col.visible;
            return acc;
        }, {} as Record<string, boolean>);

        setColumnOrder(newOrder);
        setColumnVisibility(newVisibility);
    };

    const handleResetColumns = () => {
        setColumnOrder(defaultPreferences.columnOrder);
        setColumnWidths(defaultPreferences.columnWidths);
        setColumnVisibility(defaultPreferences.columnVisibility);
        setSortColumn(defaultPreferences.sortColumn);
        setSortOrder(defaultPreferences.sortOrder);

        // Also reset filters
        setSearch("");
        setSelectedClinicId(0);
        setSelectedUser("");
        setSelectedIssue("");
        setSelectedIssueType("");
        setSelectedSeverity("");

        // Clear saved view
        setCurrentViewId(null);
        clearLastViewId('tasks');

        resetToDefaults();
    };

    // View management handlers
    const handleUpdateView = async () => {
        if (!baseViewId) return;

        const viewData: ListViewData = {
            columnOrder,
            columnWidths,
            columnVisibility,
            sortColumn,
            sortOrder,
            filters: {
                search,
                selectedClinicId,
                selectedUser,
                selectedIssue,
                selectedIssueType,
                selectedSeverity,
            },
        };

        try {
            await updateListView({
                id: baseViewId,
                data: viewData,
            });
            setCurrentViewId(baseViewId);
            saveLastViewId('tasks', baseViewId); hasLoadedInitialView.current = true;
        } catch (error) {
            console.error('Failed to update view:', error);
        }
    };

    const handleSaveAsView = async (name: string, type: 'private' | 'public', description?: string, isDefault?: boolean) => {
        const viewData: ListViewData = {
            columnOrder,
            columnWidths,
            columnVisibility,
            sortColumn,
            sortOrder,
            description,
            filters: {
                search,
                selectedClinicId,
                selectedUser,
                selectedIssue,
                selectedIssueType,
                selectedSeverity,
            },
            ...(isDefault && { is_default: true }),
        };

        try {
            const newView = await createListView({
                name,
                owner_id: user?.id as number,
                type,
                list_name: 'tasks',
                data: viewData,
            });
            setCurrentViewId(newView.id);
            setBaseViewId(newView.id);
            saveLastViewId('tasks', newView.id);
            hasLoadedInitialView.current = true;
            setIsSaveViewModalOpen(false);
        } catch (error) {
            console.error('Failed to save view:', error);
        }
    };

    const handleLoadView = (view: ListView) => {
        setCurrentViewId(view.id);
        setBaseViewId(view.id);
        // Merge in any columns added since this view was saved so they still appear in Manage Columns.
        const savedOrder = view.data.columnOrder || [];
        const missingColumns = defaultPreferences.columnOrder.filter(id => !savedOrder.includes(id));
        setColumnOrder([...savedOrder, ...missingColumns]);
        setColumnWidths(view.data.columnWidths);
        setColumnVisibility({ ...defaultPreferences.columnVisibility, ...view.data.columnVisibility });
        setSortColumn(view.data.sortColumn);
        setSortOrder(view.data.sortOrder);

        // Check if clinic will change
        const newClinicId = view.data.filters?.selectedClinicId ?? 0;
        const clinicChanged = newClinicId !== selectedClinicId;

        // Always apply filters from the view (or clear if not defined)
        if (view.data.filters) {
            setSearch(view.data.filters.search ?? "");
            setSelectedClinicId(newClinicId);
            setSelectedUser(view.data.filters.selectedUser ?? "");
            setSelectedIssue(view.data.filters.selectedIssue ?? "");
            setSelectedIssueType(view.data.filters.selectedIssueType ?? "");
            setSelectedSeverity(view.data.filters.selectedSeverity ?? "");
        } else {
            // Clear filters if the view doesn't have any defined
            setSearch("");
            setSelectedClinicId(0);
            setSelectedUser("");
            setSelectedIssue("");
            setSelectedIssueType("");
            setSelectedSeverity("");
        }

        setCurrentViewId(view.id);
        saveLastViewId('tasks', view.id);

        // Refetch data if clinic changed
        if (clinicChanged) {
            // Use setTimeout to ensure state updates are processed first
            setTimeout(() => {
                tasksQuery.refetch();
            }, 0);
        }
    };

    const handleDeleteView = async (viewId: number) => {
        try {
            await deleteListView(viewId);
            if (currentViewId === viewId) {
                handleResetColumns();
                setCurrentViewId(null);
            }
        } catch (error) {
            console.error('Failed to delete view:', error);
        }
    };

    const renderTasksTab = () => {
        if (tasksQuery.isLoading) {
            return (
                <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600 mt-4">Loading tasks...</p>
                </div>
            );
        }

        if (tasksQuery.isError) {
            return (
                <div className="text-center py-12 text-red-600">
                    <p>Error loading tasks. Please try again.</p>
                    <CustomButton
                        variant="secondary"
                        size="sm"
                        onClick={handleRefresh}
                        className="mt-4"
                    >
                        Retry
                    </CustomButton>
                </div>
            );
        }

        const tasks = filteredAndSortedTasks;

        return (
            <div className="space-y-4">
                {tasks.length === 0 ? (
                    <div className="text-center py-12 text-gray-600">
                        <p>No tasks found</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {columnOrder.filter(columnId => columnVisibility[columnId]).map(columnId => {
                                        const columnConfig: Record<string, { label: string; nonSortable?: boolean }> = {
                                            "issue": { label: "Issue" },
                                            "issue_type": { label: "Issue Type" },
                                            "issue_severity": { label: "Severity" },
                                            "issue_date": { label: "Issue date" },
                                            "HM_ID": { label: "HM ID" },
                                            "first_name": { label: "First name" },
                                            "last_name": { label: "Last name" },
                                            "case_manager_id": { label: "Case Manager" },
                                            "doctor_id": { label: "Psychiatrist" },
                                            "meeting_count_total_text": { label: "Meeting Number", nonSortable: true },
                                            "next_meeting_start": { label: "Next meeting" },
                                            "next_meeting_team_url": { label: "Next meeting link", nonSortable: true },
                                            "task": { label: "Notes", nonSortable: true },
                                            "main_phone": { label: "Main phone" },
                                            "patient_type": { label: "Age group" },
                                            "status": { label: "Status" },
                                            "email": { label: "Email" },
                                        };

                                        const config = columnConfig[columnId];
                                        return (
                                            <ResizableHeader
                                                key={columnId}
                                                column={columnId}
                                                label={config.label}
                                                nonSortable={config.nonSortable}
                                            />
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {tasks.map((task: any) => {
                                    const caseManagerName = getUserName(task.case_manager_id);
                                    const doctorName = getUserName(task.doctor_id);
                                    const ageGroupColor = getAgeGroupColor(task.patient_type);
                                    const severityColor = getSeverityColor(task.issue_severity);
                                    const issueDate = formatDate(task.issue_date);
                                    const nextMeeting = formatNextMeetingDateTime(task.next_meeting_start);

                                    const getCellContent = (columnId: string) => {
                                        switch (columnId) {
                                            case "issue":
                                                const issueText = task.issue || "-";

                                                const meetingIssues = [
                                                    "Set Case Manager Follow-Up meeting",
                                                    "Set Psychiatric Follow-Up meeting",
                                                    "Set Psychiatric Intake meeting",
                                                    "Set Case Manager Intake meeting"
                                                ];

                                                if (meetingIssues.includes(issueText) && canScheduleMeeting(user.type)) {
                                                    return (
                                                        <button
                                                            data-ai-id="task-open-new-meeting"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTaskForNewMeeting(task);
                                                                setIsNewMeetingModalOpen(true);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none text-left"
                                                        >
                                                            {issueText}
                                                        </button>
                                                    );
                                                }

                                                if (issueText === "Parent 2 didn't sign") {
                                                    return (
                                                        <button
                                                            data-ai-id="task-open-parent2-sign"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setParent2SignLink(task.parent_2_sign_link || "");
                                                                setIsParent2SignModalOpen(true);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none text-left"
                                                        >
                                                            {issueText}
                                                        </button>
                                                    );
                                                }

                                                if (issueText === "Single Guardian Verification") {
                                                    return (
                                                        <button
                                                            data-ai-id="task-open-single-guardian"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTaskForSingleGuardian(task);
                                                                setIsSingleGuardianModalOpen(true);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none text-left"
                                                        >
                                                            {issueText}
                                                        </button>
                                                    );
                                                }

                                                if (issueText === "Send & Approve summary" || issueText === "Update In Clicks" || issueText === "Add Prescription To Clicks") {
                                                    return (
                                                        <button
                                                            data-ai-id="task-open-psy-summary"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTaskForPSYSummary(task);
                                                                setIsPSYSummaryModalOpen(true);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none text-left"
                                                        >
                                                            {issueText}
                                                        </button>
                                                    );
                                                }

                                                // Show PSY summary for "Approve summary" if meeting type is psychiatric (2 or 3)
                                                if (issueText === "Approve summary" && (task.issue_meetingtype_id === 2 || task.issue_meetingtype_id === 3)) {
                                                    return (
                                                        <button
                                                            data-ai-id="task-approve-psy-summary"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTaskForPSYSummary(task);
                                                                setIsPSYSummaryModalOpen(true);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none text-left"
                                                        >
                                                            {issueText}
                                                        </button>
                                                    );
                                                }

                                                // Show CM summary for "Approve summary" if meeting type is case manager (1 or 4)
                                                if (issueText === "Approve summary" && (task.issue_meetingtype_id === 1 || task.issue_meetingtype_id === 4)) {
                                                    return (
                                                        <button
                                                            data-ai-id="task-open-cm-summary"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTaskForCMSummary(task);
                                                                setIsCMSummaryModalOpen(true);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none text-left"
                                                        >
                                                            {issueText}
                                                        </button>
                                                    );
                                                }

                                                // Empty Email opens the same editor as Invalid
                                                // Email: both are fixed by typing an address.
                                                if (issueText === "Invalid Email" || issueText === "Empty Email") {
                                                    return (
                                                        <button
                                                            data-ai-id="task-open-edit-email"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTaskForEditEmail(task);
                                                                setIsEditEmailModalOpen(true);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none text-left"
                                                        >
                                                            {issueText}
                                                        </button>
                                                    );
                                                }

                                                // Opens the patient details form, where the date of
                                                // birth is entered. The task row is a summary, so the
                                                // full record is fetched first - same as the patient
                                                // name button does.
                                                if (issueText === "Missing Date Of Birth") {
                                                    return (
                                                        <button
                                                            data-ai-id="task-open-edit-dob"
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                setIsLoadingPatientData(true);
                                                                try {
                                                                    const patientData = await apiService.getPatientPage({
                                                                        token,
                                                                        patient_id: task.id
                                                                    });
                                                                    setSelectedTaskForEditInfo(patientData);
                                                                    setIsEditPatientModalOpen(true);
                                                                } catch (error) {
                                                                    console.error('Failed to fetch patient data:', error);
                                                                } finally {
                                                                    setIsLoadingPatientData(false);
                                                                }
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none text-left"
                                                            disabled={isLoadingPatientData}
                                                        >
                                                            {issueText}
                                                        </button>
                                                    );
                                                }

                                                // Show Approve Maccabi popup for "Obligation - Review Needed" if clinic_id is 3 or 22
                                                if (issueText === "Obligation - Review Needed" && (task.clinic_id === 3 || task.clinic_id === 22)) {
                                                    return (
                                                        <button
                                                            data-ai-id="task-open-approve-maccabi"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTaskForApproveMaccabi(task);
                                                                setIsApproveMaccabiModalOpen(true);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none text-left"
                                                        >
                                                            {issueText}
                                                        </button>
                                                    );
                                                }

                                                // Show Approve Clalit popup for "Obligation - Review Needed" if clinic_id is 27
                                                if (issueText === "Obligation - Review Needed" && task.clinic_id === 27) {
                                                    return (
                                                        <button
                                                            data-ai-id="task-open-approve-clalit"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTaskForApproveClalit(task);
                                                                setIsApproveClalitModalOpen(true);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none text-left"
                                                        >
                                                            {issueText}
                                                        </button>
                                                    );
                                                }

                                                // Show Maccabi Transfer popup for "Maccabi Transfer"
                                                if (issueText === "Maccabi Transfer") {
                                                    return (
                                                        <button
                                                            data-ai-id="task-open-maccabi-transfer"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTaskForMaccabiTransfer(task);
                                                                setIsMaccabiTransferModalOpen(true);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none text-left"
                                                        >
                                                            {issueText}
                                                        </button>
                                                    );
                                                }

                                                // Show Taliaz Transfer popup for "Taliaz Transfer Intake" or "Taliaz Transfer Followup"
                                                if (issueText === "Taliaz Transfer Intake" || issueText === "Taliaz Transfer Followup") {
                                                    return (
                                                        <button
                                                            data-ai-id="task-open-taliaz-transfer"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTaskForTaliazTransfer(task);
                                                                setIsTaliazTransferModalOpen(true);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none text-left"
                                                        >
                                                            {issueText}
                                                        </button>
                                                    );
                                                }

                                                // Navigate to patient page for "Finish Meeting"
                                                if (issueText === "Finish Meeting") {
                                                    return (
                                                        <button
                                                            data-ai-id="task-open-meeting"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onNavigate?.("patient", task.id, task.issue_meeting_id);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none text-left"
                                                        >
                                                            {issueText}
                                                        </button>
                                                    );
                                                }

                                                // Show Missing Documents modal
                                                if (issueText.toLowerCase().trim() === "missing documents") {
                                                    return (
                                                        <button
                                                            data-ai-id="task-open-missing-documents"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTaskForMissingDocuments(task);
                                                                setIsMissingDocumentsModalOpen(true);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none text-left"
                                                        >
                                                            {issueText}
                                                        </button>
                                                    );
                                                }

                                                // Show the obligation approval modal for psychiatric opinion requests
                                                if (task.issue === "Psychiatric Opinion Request") {
                                                    return (
                                                        <button
                                                            data-ai-id="task-open-opinion-obligation"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openMedicalOpinionTask(task, 'obligation');
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none text-left"
                                                        >
                                                            {issueText}
                                                        </button>
                                                    );
                                                }

                                                // Show the drafting modal for the psychiatrist's letter
                                                if (task.issue === "Psychiatric Opinion Letter") {
                                                    return (
                                                        <button
                                                            data-ai-id="task-open-opinion-letter"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openMedicalOpinionTask(task, 'letter');
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none text-left"
                                                        >
                                                            {issueText}
                                                        </button>
                                                    );
                                                }

                                                // Show Case Closure modal for finish-treatment approval tasks
                                                if (task.issue_type === "Case Closure") {
                                                    return (
                                                        <button
                                                            data-ai-id="task-open-case-closure"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTaskForCaseClosure(task);
                                                                setIsCaseClosureModalOpen(true);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none text-left"
                                                        >
                                                            {issueText}
                                                        </button>
                                                    );
                                                }

                                                return issueText;
                                            case "issue_type":
                                                return task.issue_type || "-";
                                            case "issue_severity":
                                                return task.issue_severity ? (
                                                    <span
                                                        className="px-2 py-1 text-xs rounded text-white"
                                                        style={{ backgroundColor: severityColor }}
                                                    >
                                                        {task.issue_severity}
                                                    </span>
                                                ) : "-";
                                            case "issue_date":
                                                return issueDate || "-";
                                            case "HM_ID":
                                                return task.HM_ID ? (
                                                    <button
                                                        data-ai-id="task-patient-id-link"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onNavigate?.("patient", task.id);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none"
                                                    >
                                                        {task.HM_ID}
                                                    </button>
                                                ) : "-";
                                            case "first_name":
                                                return task.first_name ? (
                                                    <button
                                                        data-ai-id="task-patient-name-link"
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            setIsLoadingPatientData(true);

                                                            // Small delay to ensure spinner is visible
                                                            await new Promise(resolve => setTimeout(resolve, 100));

                                                            try {
                                                                const patientData = await apiService.getPatientPage({
                                                                    token,
                                                                    patient_id: task.id
                                                                });
                                                                setSelectedTaskForEditInfo(patientData);
                                                                setIsEditPatientModalOpen(true);
                                                            } catch (error) {
                                                                console.error('Failed to fetch patient data:', error);
                                                            } finally {
                                                                setIsLoadingPatientData(false);
                                                            }
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none"
                                                        disabled={isLoadingPatientData}
                                                    >
                                                        {task.first_name}
                                                    </button>
                                                ) : "-";
                                            case "last_name":
                                                return task.last_name || "-";
                                            case "case_manager_id":
                                                return caseManagerName || "-";
                                            case "doctor_id":
                                                return doctorName || "-";
                                            case "meeting_count_total_text":
                                                return task.meeting_count_total_text ? (
                                                    <button
                                                        data-ai-id="task-meeting-count-link"
                                                        onClick={() => {
                                                            setSelectedTaskForMeetings(task);
                                                            setIsMeetingCountModalOpen(true);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 underline cursor-pointer bg-transparent border-none"
                                                    >
                                                        {task.meeting_count_total_text}
                                                    </button>
                                                ) : "-";
                                            case "next_meeting_start":
                                                return nextMeeting || "-";
                                            case "next_meeting_team_url":
                                                return task.next_meeting_team_url ? (
                                                    <a
                                                        href={task.next_meeting_team_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-600 hover:text-blue-800 underline"
                                                    >
                                                        Link
                                                    </a>
                                                ) : "-";
                                            case "task":
                                                const notes = task.task && task.task.trim() !== '' ? task.task : 'Empty';
                                                return (
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedTaskForEdit(task);
                                                                setIsEditCommentsModalOpen(true);
                                                            }}
                                                            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                                                            title="Edit notes"
                                                        >
                                                            ✏️
                                                        </button>
                                                        <span className="truncate max-w-xs" title={notes}>{notes}</span>
                                                    </div>
                                                );
                                            case "main_phone":
                                                return task.main_phone || "-";
                                            case "patient_type":
                                                return task.patient_type ? (
                                                    <span
                                                        className="px-2 py-1 text-xs rounded text-white"
                                                        style={{ backgroundColor: ageGroupColor }}
                                                    >
                                                        {task.patient_type}
                                                    </span>
                                                ) : "-";
                                            case "status":
                                                return task.status ? (
                                                    <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700">
                                                        {task.status}
                                                    </span>
                                                ) : "-";
                                            case "email":
                                                return task.email || "-";
                                            default:
                                                return "-";
                                        }
                                    };

                                    return (
                                        <tr
                                            key={getTaskKey(task)}
                                            className="hover:bg-gray-50 cursor-pointer"
                                            onClick={(e) => {
                                                // Don't open context menu if clicking on buttons or links
                                                const target = e.target as HTMLElement;
                                                if (
                                                    target.tagName === 'BUTTON' ||
                                                    target.tagName === 'A' ||
                                                    target.closest('button') ||
                                                    target.closest('a')
                                                ) {
                                                    return;
                                                }

                                                setContextMenu({
                                                    x: e.clientX,
                                                    y: e.clientY,
                                                    task: task
                                                });
                                            }}
                                        >
                                            {columnOrder.filter(columnId => columnVisibility[columnId]).map(columnId => {
                                                const isNotesCol = columnId === "task";
                                                const cellContent = getCellContent(columnId);

                                                return (
                                                    <td
                                                        key={columnId}
                                                        className={`px-4 py-3 ${isNotesCol ? 'overflow-hidden text-ellipsis' : ''} whitespace-nowrap text-sm text-gray-900`}
                                                        style={isNotesCol ? { maxWidth: columnWidths[columnId] || '300px' } : {}}
                                                        title={isNotesCol && typeof cellContent === 'string' ? cellContent : undefined}
                                                    >
                                                        {cellContent}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Layout
            userName={user?.name || user?.email || "User"}
            onLogout={onLogout}
            activeMenuItem="tasks"
            onNavigate={onNavigate}
            institutionId={user.institution_id || 0}
            user={user}
            taskCount={taskCount}
            whatsappUnreadCount={whatsappUnreadCount}
        >
            {/* Tasks Section */}
            <div className="bg-white rounded-lg shadow mb-8">
                <div className="border-b border-gray-200 px-6 py-4">
                    {/* Header row with title and view selector */}
                    <div className="flex items-center justify-between gap-3 mb-4">
                        <div className="flex items-center gap-3">
                            <h2 className="text-lg font-semibold text-gray-900">Tasks({filteredAndSortedTasks.length})</h2>
                            <ViewSelector
                                privateViews={privateViews}
                                publicViews={publicViews}
                                currentViewId={currentViewId}
                                baseViewId={baseViewId}
                                onSelectView={handleLoadView}
                                onSaveView={handleUpdateView}
                                onSaveAsView={() => setIsSaveViewModalOpen(true)}
                                onDeleteView={handleDeleteView}
                                isLoading={isLoadingViews}
                                isAdmin={isAdmin}
                            />
                        </div>
                        <CustomButton
                            variant="secondary"
                            size="sm"
                            onClick={handleManageColumns}
                        >
                            ⚙️ Manage Columns
                        </CustomButton>
                    </div>

                    {/* Controls section */}
                    <div className="space-y-4">
                        {/* First row: Search and Clinic */}
                        <div className="flex gap-4 items-center">
                            <div className="flex-1">
                                <input
                                    id="search"
                                    type="text"
                                    placeholder="Search by name, HM ID, or issue..."
                                    value={search}
                                    onChange={handleSearchChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div className="w-64">
                                <select
                                    id="clinic-select"
                                    value={selectedClinicId}
                                    onChange={(e) => setSelectedClinicId(Number(e.target.value))}
                                    disabled={false}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                >
                                    <option value={0}>All Clinics</option>
                                    {clinics.map((clinic: any) => (
                                        <option key={clinic.id} value={clinic.id}>
                                            {clinic.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Second row: Filters */}
                        <div className="flex gap-4 items-center">
                            {/* User filter - only show for admins */}
                            {isAdmin && (
                                <div className="flex-1">
                                    <select
                                        id="user-filter"
                                        value={selectedUser}
                                        onChange={(e) => {
                                            const newSelectedUser = e.target.value === "" ? "" : Number(e.target.value);
                                            setSelectedUser(newSelectedUser);
                                            if (newSelectedUser !== "" && onlyMy) {
                                                setOnlyMy(false);
                                            }
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">All Users</option>
                                        {filterOptions.users.map((userId) => (
                                            <option key={userId} value={userId}>
                                                {getUserName(userId)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Only my checkbox - only show for admins */}
                            {isAdmin && (
                                <label className="w-auto flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={onlyMy}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setOnlyMy(checked);
                                            if (checked && selectedUser !== "") {
                                                setSelectedUser("");
                                            }
                                        }}
                                        className="cursor-pointer"
                                    />
                                    <span className="text-sm text-gray-600">Only mine</span>
                                </label>
                            )}

                            <div className="flex-1">
                                <select
                                    id="issue-filter"
                                    value={selectedIssue}
                                    onChange={(e) => setSelectedIssue(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">All Issues</option>
                                    {filterOptions.issues.map((issue) => (
                                        <option key={issue} value={issue}>
                                            {issue}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex-1">
                                <select
                                    id="issue-type-filter"
                                    value={selectedIssueType}
                                    onChange={(e) => setSelectedIssueType(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">All Issue Types</option>
                                    {filterOptions.issueTypes.map((issueType) => (
                                        <option key={issueType} value={issueType}>
                                            {issueType}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex-1">
                                <select
                                    id="severity-filter"
                                    value={selectedSeverity}
                                    onChange={(e) => setSelectedSeverity(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">All Severities</option>
                                    {filterOptions.severities.map((severity) => (
                                        <option key={severity} value={severity}>
                                            {severity}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <CustomButton
                                variant="secondary"
                                size="sm"
                                onClick={handleClearFilters}
                            >
                                Clear Filters
                            </CustomButton>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {renderTasksTab()}
                </div>
            </div>

            {/* Manage Columns Modal */}
            <ManageColumnsModal
                isOpen={isManageColumnsOpen}
                onClose={() => setIsManageColumnsOpen(false)}
                columns={columnOrder.map(id => ({
                    id,
                    label: ALL_COLUMNS.find(col => col.id === id)?.label || id,
                    visible: columnVisibility[id] ?? true,
                }))}
                onSave={handleSaveColumns}
                onReset={handleResetColumns}
            />

            {/* Save As View Modal */}
            <SaveViewModal
                isOpen={isSaveViewModalOpen}
                onClose={() => setIsSaveViewModalOpen(false)}
                onSave={handleSaveAsView}
                currentConfig={{
                    columnOrder,
                    columnWidths,
                    columnVisibility,
                    sortColumn,
                    sortOrder,
                }}
                isAdmin={isAdmin}
                isSaving={isCreating}
                existingDefaultView={defaultView}
            />

            {/* Meeting Count Modal */}
            <MeetingCountModal
                isOpen={isMeetingCountModalOpen}
                onClose={() => {
                    setIsMeetingCountModalOpen(false);
                    setSelectedTaskForMeetings(null);
                }}
                caseManagerIntake={selectedTaskForMeetings?.meeting_count_text1 || "0"}
                psychiatricIntake={selectedTaskForMeetings?.meeting_count_text2 || "0"}
                psyFollowUp={selectedTaskForMeetings?.meeting_count_text3 || "0"}
                caseManagerFollowUp={selectedTaskForMeetings?.meeting_count_text4 || "0"}
            />


            {/* Context Menu */}
            {contextMenu && (() => {
                const hasPhoneExt = user.phone_ext && user.phone_ext.trim() !== '';
                const taskPhone = contextMenu.task.main_phone;
                const isNeedPrepTask = contextMenu.task.issue === 'Need Prep call';

                return (
                    <ContextMenu
                        x={contextMenu.x}
                        y={contextMenu.y}
                        options={[
                            // Ready option for "Need Prep" tasks
                            ...(isNeedPrepTask ? [{
                                label: 'Ready',
                                icon: '✅',
                                onClick: async () => {
                                    setContextMenu(null);
                                    setStatusMessage({ type: 'loading', text: 'Updating prep status...' });
                                    try {
                                        const task = contextMenu.task;
                                        await apiService.updateMeetingPrepStatus(
                                            token,
                                            task.issue_meeting_id,
                                            'ready',
                                            task.issue_meetingtype_id || 0,
                                            task.clinic_id || selectedClinicId,
                                            false
                                        );
                                        console.log('[Tasks] Marked meeting as ready');
                                        setStatusMessage({ type: 'success', text: 'Meeting marked as ready' });
                                        setTimeout(() => setStatusMessage(null), 3000);
                                    } catch (error: any) {
                                        console.error('Failed to update prep status:', error);
                                        const errorMsg = error.response?.data?.message || error.message || 'An error occurred';
                                        setStatusMessage({ type: 'error', text: `Failed to update prep status: ${errorMsg}` });
                                        setTimeout(() => setStatusMessage(null), 5000);
                                    }
                                }
                            }] : []),
                            // No Answer option for "Need Prep" tasks
                            ...(isNeedPrepTask ? [{
                                label: 'No Answer',
                                icon: '📵',
                                onClick: async () => {
                                    setContextMenu(null);
                                    setStatusMessage({ type: 'loading', text: 'Updating prep status...' });
                                    try {
                                        const task = contextMenu.task;
                                        await apiService.updateMeetingPrepStatus(
                                            token,
                                            task.issue_meeting_id,
                                            'no_answer',
                                            task.issue_meetingtype_id || 0,
                                            task.clinic_id || selectedClinicId,
                                            false
                                        );
                                        console.log('[Tasks] Marked meeting as no answer');
                                        setStatusMessage({ type: 'success', text: 'Meeting marked as no answer' });
                                        setTimeout(() => setStatusMessage(null), 3000);
                                    } catch (error: any) {
                                        console.error('Failed to update prep status:', error);
                                        const errorMsg = error.response?.data?.message || error.message || 'An error occurred';
                                        setStatusMessage({ type: 'error', text: `Failed to update prep status: ${errorMsg}` });
                                        setTimeout(() => setStatusMessage(null), 5000);
                                    }
                                }
                            }] : []),
                            {
                                label: 'Edit Patient',
                                icon: '✏️',
                                onClick: async () => {
                                    const task = contextMenu.task;
                                    setContextMenu(null);
                                    setIsLoadingPatientData(true);
                                    try {
                                        const patientData = await apiService.getPatientPage({
                                            token,
                                            patient_id: task.id
                                        });
                                        setSelectedTaskForEditInfo(patientData);
                                        setIsEditPatientModalOpen(true);
                                    } catch (error) {
                                        console.error('Failed to fetch patient data:', error);
                                    } finally {
                                        setIsLoadingPatientData(false);
                                    }
                                }
                            },
                            ...(canScheduleMeeting(user.type) ? [{
                                label: 'New Meeting',
                                icon: '📅',
                                onClick: () => {
                                    setSelectedTaskForNewMeeting(contextMenu.task);
                                    setIsNewMeetingModalOpen(true);
                                    setContextMenu(null);
                                }
                            }] : []),
                            {
                                label: 'View Meetings',
                                icon: '📋',
                                onClick: () => {
                                    setSelectedTaskForViewMeetings(contextMenu.task);
                                    setIsViewMeetingsModalOpen(true);
                                    setContextMenu(null);
                                }
                            },
                            {
                                label: 'View Patient',
                                icon: '👤',
                                onClick: () => {
                                    const task = contextMenu.task;
                                    setContextMenu(null);
                                    // Navigate to patient page
                                    onNavigate?.("patient", task.id);
                                }
                            },
                            ...(hasPhoneExt && taskPhone ? [{
                                label: 'Call',
                                icon: '📞',
                                onClick: async () => {
                                    const task = contextMenu.task;
                                    if (task && task.main_phone) {
                                        setContextMenu(null);
                                        setStatusMessage({ type: 'loading', text: 'Initiating call...' });
                                        try {
                                            const response = await apiService.makeCall(token, task.id, task.main_phone);
                                            const successMsg = response.message || 'Call initiated successfully';
                                            setStatusMessage({ type: 'success', text: successMsg });
                                            setTimeout(() => setStatusMessage(null), 3000);
                                        } catch (error: any) {
                                            console.error('Failed to make call:', error);
                                            // Extract error message - prioritize error.message (from thrown errors) over response data
                                            const errorMsg = error.message || error.response?.data?.message || 'An error occurred';
                                            setStatusMessage({ type: 'error', text: errorMsg });
                                            setTimeout(() => setStatusMessage(null), 5000);
                                        }
                                    }
                                }
                            }] : [])
                        ]}
                        onClose={() => setContextMenu(null)}
                    />
                );
            })()}

            {/* New Meeting Modal */}
            <NewMeetingModal
                isOpen={isNewMeetingModalOpen}
                onClose={() => {
                    setIsNewMeetingModalOpen(false);
                    setSelectedTaskForNewMeeting(null);
                }}
                patient={selectedTaskForNewMeeting ? {
                    id: selectedTaskForNewMeeting.id,
                    HM_ID: selectedTaskForNewMeeting.HM_ID,
                    first_name: selectedTaskForNewMeeting.first_name,
                    last_name: selectedTaskForNewMeeting.last_name,
                    doctor_id: selectedTaskForNewMeeting.doctor_id,
                    case_manager_id: selectedTaskForNewMeeting.case_manager_id,
                    default_doctor_id: selectedTaskForNewMeeting.default_doctor_id,
                    default_case_manager_id: selectedTaskForNewMeeting.default_case_manager_id,
                    next_meeting_type_id: selectedTaskForNewMeeting.next_meeting_type_id,
                    set_meeting_warnings: selectedTaskForNewMeeting.set_meeting_warnings
                } : null}
                users={users}
                meetingTypes={propsMeetingTypes}
                currentUser={user}
            />

            {/* Edit Patient Modal */}
            <EditPatientModal
                isOpen={isEditPatientModalOpen}
                onClose={() => {
                    setIsEditPatientModalOpen(false);
                    setSelectedTaskForEditInfo(null);
                }}
                patient={selectedTaskForEditInfo}
                onSave={async (updatedData: any) => {
                    setIsSavingPatientInfo(true);
                    try {
                        await apiService.updatePatientInfo(
                            token,
                            selectedTaskForEditInfo.id,
                            updatedData
                        );
                        // Refresh task data
                        await tasksQuery.refetch();
                        // Close modal
                        setIsEditPatientModalOpen(false);
                        setSelectedTaskForEditInfo(null);
                    } catch (error: any) {
                        console.error('Failed to update patient info:', error);
                        throw new Error(error.response?.data?.message || 'Failed to update patient information');
                    } finally {
                        setIsSavingPatientInfo(false);
                    }
                }}
                isSaving={isSavingPatientInfo}
            />

            {/* View Meetings Modal */}
            <ViewMeetingsModal
                isOpen={isViewMeetingsModalOpen}
                onClose={() => {
                    setIsViewMeetingsModalOpen(false);
                    setSelectedTaskForViewMeetings(null);
                }}
                patientId={selectedTaskForViewMeetings?.id || ''}
                patientName={selectedTaskForViewMeetings ? `${selectedTaskForViewMeetings.first_name} ${selectedTaskForViewMeetings.last_name}` : ''}
                user={user}
                token={token}
                users={users as any}
                meetingTypes={propsMeetingTypes}
            />

            {/* Parent 2 Sign Modal */}
            <Parent2SignModal
                isOpen={isParent2SignModalOpen}
                onClose={() => setIsParent2SignModalOpen(false)}
                signLink={parent2SignLink}
            />

            {/* Edit Email Modal */}
            <EditEmailModal
                isOpen={isEditEmailModalOpen}
                onClose={() => {
                    setIsEditEmailModalOpen(false);
                    setSelectedTaskForEditEmail(null);
                }}
                /* own_email_field names the address that belongs on this patient -
                   the one the Empty Email task checks and update_new_email writes.
                   It comes from the backend (patient_utils.own_email_field) rather
                   than being re-derived here, so the label cannot drift from the
                   field that actually gets written. */
                fieldLabel={selectedTaskForEditEmail?.own_email_field === 'parent_1_email'
                    ? 'Parent1 Email'
                    : 'Patient Email'}
                initialEmail={(selectedTaskForEditEmail?.own_email_field === 'parent_1_email'
                    ? selectedTaskForEditEmail?.parent_1_email
                    : selectedTaskForEditEmail?.email) || ''}
                onSave={async (newEmail: string) => {
                    setIsSavingEmail(true);
                    try {
                        await apiService.updateEmail(
                            token,
                            selectedTaskForEditEmail.id,
                            newEmail
                        );
                        // Refresh task data
                        await tasksQuery.refetch();
                        // Close modal
                        setIsEditEmailModalOpen(false);
                        setSelectedTaskForEditEmail(null);
                    } catch (error: any) {
                        console.error('Failed to update email:', error);
                        throw new Error(error.response?.data?.message || 'Failed to update email address');
                    } finally {
                        setIsSavingEmail(false);
                    }
                }}
                isSaving={isSavingEmail}
            />

            {/* PSY Summary Modal */}
            <PSYSummaryModal
                isOpen={isPSYSummaryModalOpen}
                onClose={() => {
                    setIsPSYSummaryModalOpen(false);
                    setSelectedTaskForPSYSummary(null);
                    // Clear status message when modal closes
                    setStatusMessage(null);
                }}
                meetingId={selectedTaskForPSYSummary?.issue_meeting_id}
                clinicId={selectedTaskForPSYSummary?.clinic_id}
                token={token}
                medicationTypes={medicationTypes}
                diagnosisCodes={propsDiagnosisCodes || []}
                onSuccess={() => {
                    // Remove the task from the list
                    if (selectedTaskForPSYSummary) {
                        removeTaskFromList(selectedTaskForPSYSummary);
                    }
                }}
                onStatusMessage={setStatusMessage}
                taskIssue={selectedTaskForPSYSummary?.issue}
                user={users.find(u => u.id === selectedTaskForPSYSummary?.doctor_id)}
            />

            {/* CM Summary Modal */}
            <CMSummaryModal
                isOpen={isCMSummaryModalOpen}
                onClose={() => {
                    setIsCMSummaryModalOpen(false);
                    setSelectedTaskForCMSummary(null);
                    // Clear status message when modal closes
                    setStatusMessage(null);
                }}
                meetingId={selectedTaskForCMSummary?.issue_meeting_id}
                token={token}
                onSuccess={() => {
                    // Remove the task from the list
                    if (selectedTaskForCMSummary) {
                        removeTaskFromList(selectedTaskForCMSummary);
                    }
                }}
                onStatusMessage={setStatusMessage}
            />

            {/* Approve Maccabi Modal */}
            <ApproveMaccabiModal
                isOpen={isApproveMaccabiModalOpen}
                onClose={() => {
                    setIsApproveMaccabiModalOpen(false);
                    setSelectedTaskForApproveMaccabi(null);
                }}
                task={selectedTaskForApproveMaccabi}
                token={token}
                onSuccess={() => {
                    // Remove the task from the list
                    if (selectedTaskForApproveMaccabi) {
                        removeTaskFromList(selectedTaskForApproveMaccabi);
                    }
                }}
                onStatusMessage={setStatusMessage}
            />

            {/* Approve Clalit Modal */}
            <ApproveClalitModal
                isOpen={isApproveClalitModalOpen}
                onClose={() => {
                    setIsApproveClalitModalOpen(false);
                    setSelectedTaskForApproveClalit(null);
                }}
                task={selectedTaskForApproveClalit}
                token={token}
                onSuccess={() => {
                    // Remove the task from the list
                    if (selectedTaskForApproveClalit) {
                        removeTaskFromList(selectedTaskForApproveClalit);
                    }
                }}
                onStatusMessage={setStatusMessage}
            />

            {/* Maccabi Transfer Modal */}
            <MaccabiTransferModal
                isOpen={isMaccabiTransferModalOpen}
                onClose={() => {
                    setIsMaccabiTransferModalOpen(false);
                    setSelectedTaskForMaccabiTransfer(null);
                }}
                task={selectedTaskForMaccabiTransfer}
                token={token}
                onSuccess={() => {
                    // Remove the task from the list
                    if (selectedTaskForMaccabiTransfer) {
                        removeTaskFromList(selectedTaskForMaccabiTransfer);
                    }
                }}
                onStatusMessage={setStatusMessage}
            />

            {/* Taliaz Transfer Modal */}
            <TaliazTransferModal
                isOpen={isTaliazTransferModalOpen}
                onClose={() => {
                    setIsTaliazTransferModalOpen(false);
                    setSelectedTaskForTaliazTransfer(null);
                }}
                task={selectedTaskForTaliazTransfer}
                token={token}
                onSuccess={() => {
                    // Remove the task from the list
                    if (selectedTaskForTaliazTransfer) {
                        removeTaskFromList(selectedTaskForTaliazTransfer);
                    }
                }}
                onStatusMessage={setStatusMessage}
            />

            {/* Edit Comments Modal */}
            <EditCommentsModal
                isOpen={isEditCommentsModalOpen}
                onClose={() => {
                    setIsEditCommentsModalOpen(false);
                    setSelectedTaskForEdit(null);
                }}
                initialNotes={selectedTaskForEdit?.task || ''}
                onSave={async (newNotes: string) => {
                    setIsSavingComments(true);
                    try {
                        await apiService.updatePatientComments(
                            token,
                            selectedTaskForEdit.id,
                            newNotes
                        );
                        // Refresh task data
                        await tasksQuery.refetch();
                        // Close modal
                        setIsEditCommentsModalOpen(false);
                        setSelectedTaskForEdit(null);
                    } catch (error: any) {
                        console.error('Failed to update comments:', error);
                        throw new Error(error.response?.data?.message || 'Failed to update comments');
                    } finally {
                        setIsSavingComments(false);
                    }
                }}
                isSaving={isSavingComments}
            />

            {/* Single Guardian Verification Modal */}
            <SingleGuardianVerificationModal
                isOpen={isSingleGuardianModalOpen}
                onClose={() => {
                    setIsSingleGuardianModalOpen(false);
                    setSelectedTaskForSingleGuardian(null);
                }}
                task={selectedTaskForSingleGuardian}
                token={token}
                onSave={async (data: { action: 'approve' | 'decline'; reason?: string; message?: string }) => {
                    setIsSavingSingleGuardian(true);
                    try {
                        console.log('[Tasks] Single guardian verification action:', data.action);
                        console.log('[Tasks] Patient ID:', selectedTaskForSingleGuardian?.id);
                        console.log('[Tasks] Decline data:', {
                            reason: data.reason,
                            message: data.message
                        });

                        // Update guardian status using dedicated endpoint
                        let response;
                        if (data.action === 'approve') {
                            response = await apiService.updateGuardianStatus({
                                token,
                                patient_id: selectedTaskForSingleGuardian.id,
                                single_guardian_verification_status: "Approved",
                                single_parent_reject_reason: "",
                                single_parent_reject_text: ""
                            });
                        }

                        if (data.action === 'decline') {
                            // Update guardian status when declining
                            const updateData = {
                                token,
                                patient_id: selectedTaskForSingleGuardian.id,
                                single_guardian_verification_status: "Rejected",
                                single_parent_reject_reason: data.reason || "",
                                single_parent_reject_text: data.message || ""
                            };
                            console.log('[Tasks] Updating patient with decline data:', updateData);

                            response = await apiService.updateGuardianStatus(updateData);
                            console.log('[Tasks] Patient updated successfully');
                        }

                        // Handle response - if op is 'delete', remove task from list
                        if (response?.op === 'delete') {
                            console.log('[Tasks] Task will be removed from list');
                            removeTaskFromList(selectedTaskForSingleGuardian);
                        }

                        // Refresh task data
                        await tasksQuery.refetch();

                        // Close modal
                        setIsSingleGuardianModalOpen(false);
                        setSelectedTaskForSingleGuardian(null);

                        // Show success message
                        const actionText = data.action === 'approve' ? 'approved' : 'declined';
                        setStatusMessage({
                            type: 'success',
                            text: `Single guardian verification ${actionText} successfully`
                        });
                        setTimeout(() => setStatusMessage(null), 3000);
                    } catch (error: any) {
                        console.error('[Tasks] Failed to save single guardian verification:', error);
                        console.error('[Tasks] Error details:', {
                            message: error.message,
                            response: error.response?.data,
                            status: error.response?.status
                        });

                        // Show error message to user
                        const errorMessage = error.response?.data?.message || error.message || 'Failed to save single guardian verification';
                        setStatusMessage({
                            type: 'error',
                            text: errorMessage
                        });
                        setTimeout(() => setStatusMessage(null), 5000);

                        throw new Error(errorMessage);
                    } finally {
                        setIsSavingSingleGuardian(false);
                    }
                }}
                isSaving={isSavingSingleGuardian}
            />

            {/* Missing Documents Modal */}
            <MissingDocumentsModal
                isOpen={isMissingDocumentsModalOpen}
                onClose={() => {
                    setIsMissingDocumentsModalOpen(false);
                    setSelectedTaskForMissingDocuments(null);
                }}
                onPatientContacted={async () => {
                    if (!selectedTaskForMissingDocuments) return;

                    try {
                        console.log('[Tasks] Patient contacted for missing documents');
                        console.log('[Tasks] Patient ID:', selectedTaskForMissingDocuments.id);

                        // Call API to update patient contacted status
                        const response = await apiService.updateUploadFilesContacted({
                            token,
                            patient_id: selectedTaskForMissingDocuments.id,
                        });

                        console.log('[Tasks] Patient contacted updated successfully');

                        // Handle response - if op is 'delete', remove task from list
                        if (response?.op === 'delete') {
                            console.log('[Tasks] Task will be removed from list');
                            removeTaskFromList(selectedTaskForMissingDocuments);
                        }

                        // Refresh task data
                        await tasksQuery.refetch();

                        // Close modal
                        setIsMissingDocumentsModalOpen(false);
                        setSelectedTaskForMissingDocuments(null);

                        // Show success message
                        setStatusMessage({
                            type: 'success',
                            text: 'Patient contacted successfully'
                        });
                        setTimeout(() => setStatusMessage(null), 3000);
                    } catch (error: any) {
                        console.error('[Tasks] Failed to update patient contacted:', error);
                        console.error('[Tasks] Error details:', {
                            message: error.message,
                            response: error.response?.data,
                            status: error.response?.status
                        });

                        // Show error message to user
                        const errorMessage = error.response?.data?.message || error.message || 'Failed to update patient contacted status';
                        setStatusMessage({
                            type: 'error',
                            text: errorMessage
                        });
                        setTimeout(() => setStatusMessage(null), 5000);
                    }
                }}
            />

            {/* Case Closure Approval Modal */}
            <CaseClosureApprovalModal
                isOpen={isCaseClosureModalOpen}
                onClose={() => {
                    setIsCaseClosureModalOpen(false);
                    setSelectedTaskForCaseClosure(null);
                    setStatusMessage(null);
                }}
                patientId={selectedTaskForCaseClosure?.id}
                token={token}
                getUserName={getUserName}
                onSuccess={async () => {
                    // Status set to approved → task removed; refetch so the row disappears.
                    if (selectedTaskForCaseClosure) {
                        removeTaskFromList(selectedTaskForCaseClosure);
                    }
                    await tasksQuery.refetch();
                }}
                onStatusMessage={setStatusMessage}
            />

            {/* Medical Opinion Obligation Approval Modal */}
            <MedicalOpinionObligationApprovalModal
                isOpen={isMedicalOpinionApprovalModalOpen && !isLoadingMedicalOpinion}
                onClose={() => {
                    setIsMedicalOpinionApprovalModalOpen(false);
                    setSelectedTaskForMedicalOpinion(null);
                    setSelectedMedicalOpinion(null);
                }}
                token={token}
                opinion={selectedMedicalOpinion}
                patientName={selectedTaskForMedicalOpinion?.patient_name || ''}
                patientTz={selectedTaskForMedicalOpinion?.patient_id || ''}
                patientHmId={selectedTaskForMedicalOpinion?.HM_ID || ''}
                patientPhone={selectedTaskForMedicalOpinion?.phone || selectedTaskForMedicalOpinion?.main_phone || ''}
                parent1Phone={selectedTaskForMedicalOpinion?.parent_1_phone_number}
                parent2Phone={selectedTaskForMedicalOpinion?.parent_2_phone_number}
                clinicName={clinics.find((c: any) => c.id === selectedTaskForMedicalOpinion?.clinic_id)?.name || ''}
                isAdmin={isAdmin}
                getUserName={getUserName}
                onSuccess={async () => {
                    // Approving moves the request to in_progress and closing ends it, so the
                    // Psychiatric Opinion Request task is gone either way.
                    if (selectedTaskForMedicalOpinion) {
                        removeTaskFromList(selectedTaskForMedicalOpinion);
                    }
                    await tasksQuery.refetch();
                }}
            />

            {/* Psychiatric Opinion Letter Modal - the drafting screen, writable by the
                assigned doctor and by any admin */}
            <MedicalOpinionLetterModal
                isOpen={isMedicalOpinionLetterModalOpen && !isLoadingMedicalOpinion}
                onClose={() => {
                    setIsMedicalOpinionLetterModalOpen(false);
                    setSelectedTaskForMedicalOpinion(null);
                    setSelectedMedicalOpinion(null);
                }}
                token={token}
                opinion={selectedMedicalOpinion}
                patientName={selectedTaskForMedicalOpinion?.patient_name || ''}
                patientTz={selectedTaskForMedicalOpinion?.patient_id || ''}
                patientHmId={selectedTaskForMedicalOpinion?.HM_ID || ''}
                patientPhone={selectedTaskForMedicalOpinion?.phone || selectedTaskForMedicalOpinion?.main_phone || ''}
                parent1Phone={selectedTaskForMedicalOpinion?.parent_1_phone_number}
                parent2Phone={selectedTaskForMedicalOpinion?.parent_2_phone_number}
                clinicName={clinics.find((c: any) => c.id === selectedTaskForMedicalOpinion?.clinic_id)?.name || ''}
                currentUserId={Number(user?.id) || undefined}
                isAdmin={isAdmin}
                diagnosisCodes={propsDiagnosisCodes || []}
                medicationTypes={medicationTypes}
                getUserName={getUserName}
                onSuccess={async () => {
                    // Completion ends the request, so the Psychiatric Opinion Letter task is
                    // gone. Saving and generating the pdf leave it in place, and neither
                    // calls this.
                    if (selectedTaskForMedicalOpinion) {
                        removeTaskFromList(selectedTaskForMedicalOpinion);
                    }
                    await tasksQuery.refetch();
                }}
            />

            {/* Status Message Bar */}
            {statusMessage && (
                <div
                    className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-lg shadow-lg"
                    style={{
                        backgroundColor:
                            statusMessage.type === 'error' ? '#fee2e2' :
                                statusMessage.type === 'success' ? '#dcfce7' :
                                    '#f3f4f6',
                        color:
                            statusMessage.type === 'error' ? '#dc2626' :
                                statusMessage.type === 'success' ? '#16a34a' :
                                    '#6b7280'
                    }}
                >
                    {statusMessage.text}
                </div>
            )}

            {/* Loading Overlay */}
            {isLoadingPatientData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                    <div className="bg-white rounded-lg p-6 flex flex-col items-center shadow-2xl">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                        <p className="text-gray-700 font-medium">Loading patient data...</p>
                    </div>
                </div>
            )}
        </Layout>
    );
};
